import { useState } from "react";
import { ImageLightbox } from "./ImageLightbox";

interface FeatureSectionProps {
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
  imageCaption?: string;
  bullets?: string[];
  reverse?: boolean;
  secondaryImageSrc?: string;
  secondaryImageAlt?: string;
  secondaryImageCaption?: string;
}

export function FeatureSection({
  title,
  description,
  imageSrc,
  imageAlt,
  imageCaption,
  bullets,
  reverse = false,
  secondaryImageSrc,
  secondaryImageAlt,
  secondaryImageCaption,
}: FeatureSectionProps) {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const [lightboxAlt, setLightboxAlt] = useState("");

  const openLightbox = (src: string, alt: string) => {
    setLightboxSrc(src);
    setLightboxAlt(alt);
  };

  return (
    <section className="py-16 md:py-20">
      <div
        className={`flex flex-col ${reverse ? "lg:flex-row-reverse" : "lg:flex-row"} gap-10 lg:gap-16 items-center`}
      >
        {/* Screenshots */}
        <div className="w-full lg:w-3/5 flex-shrink-0">
          <button
            type="button"
            onClick={() => openLightbox(imageSrc, imageAlt)}
            className="block w-full text-left group"
          >
            <img
              src={imageSrc}
              alt={imageAlt}
              className="w-full rounded-xl shadow-2xl shadow-black/40 border border-dark-border transition-shadow group-hover:shadow-warm-900/30"
              loading="lazy"
            />
          </button>
          {imageCaption && (
            <p className="mt-2 text-warm-500 text-xs text-center">{imageCaption}</p>
          )}
          {secondaryImageSrc && (
            <>
              <button
                type="button"
                onClick={() => openLightbox(secondaryImageSrc, secondaryImageAlt ?? "")}
                className="block w-full text-left group mt-4"
              >
                <img
                  src={secondaryImageSrc}
                  alt={secondaryImageAlt ?? ""}
                  className="w-full rounded-xl shadow-2xl shadow-black/40 border border-dark-border transition-shadow group-hover:shadow-warm-900/30"
                  loading="lazy"
                />
              </button>
              {secondaryImageCaption && (
                <p className="mt-2 text-warm-500 text-xs text-center">{secondaryImageCaption}</p>
              )}
            </>
          )}
        </div>

        {/* Text content */}
        <div className="w-full lg:w-2/5">
          <h2 className="text-2xl md:text-3xl font-bold text-warm-50 mb-4">{title}</h2>
          <p className="text-warm-300 text-base md:text-lg leading-relaxed mb-6">{description}</p>

          {bullets && bullets.length > 0 && (
            <ul className="space-y-2">
              {bullets.map((bullet, index) => (
                <li key={index} className="flex items-start gap-2.5 text-warm-400 text-sm">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-warm-600 flex-shrink-0" />
                  {bullet}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {lightboxSrc && (
        <ImageLightbox src={lightboxSrc} alt={lightboxAlt} onClose={() => setLightboxSrc(null)} />
      )}
    </section>
  );
}
